// src/features/players/PlayerApi.ts

import { webApi } from '@/shared/api/WebApi';
import type { PlayerViewData, PlayerCreateRequest, PlayerUpdateRequest } from '@/lib/types/Player';

/**
 * Player API endpoints.
 * Static class for consistent naming and namespacing.
 */
export class PlayerApi {
  /** Fetch all players for a customer. */
  static async getPlayers(cId: number): Promise<PlayerViewData[]> {
    return webApi.get<PlayerViewData[]>('player', { cId });
  }

  /** Fetch a single player by ID. */
  static async getPlayer(cId: number, playerId: number): Promise<PlayerViewData> {
    return webApi.get<PlayerViewData>(`player/${playerId}`, { cId });
  }

  /** Create a new player. */
  static async createPlayer(cId: number, req: PlayerCreateRequest): Promise<PlayerViewData> {
    return webApi.post<PlayerViewData>('player', { cId }, req);
  }

  /** Copy an existing player. */
  static async copyPlayer(cId: number, playerId: number, req: PlayerCreateRequest): Promise<PlayerViewData> {
    return webApi.post<PlayerViewData>(`player/${playerId}/copy`, { cId }, req);
  }

  /** Update a player (full update — always submit complete IPlayerUpdateReq). */
  static async updatePlayer(cId: number, playerId: number, req: PlayerUpdateRequest): Promise<PlayerViewData> {
    return webApi.post<PlayerViewData>(`player/${playerId}`, { cId }, req);
  }

  /** Bulk activate players. */
  static async activatePlayers(cId: number, playerIds: number[]): Promise<void> {
    return webApi.post('player/activate', { cId }, { playerIds });
  }

  /** Bulk deactivate players. */
  static async deactivatePlayers(cId: number, playerIds: number[]): Promise<void> {
    return webApi.post('player/deactivate', { cId }, { playerIds });
  }

  /** Bulk delete players. */
  static async deletePlayers(cId: number, playerIds: number[]): Promise<void> {
    return webApi.post('player/delete', { cId }, { playerIds });
  }
}
